import {UIMGamePlayerInformationView} from './UIMGamePlayerInformationView'
import {UIGamePlayersController} from '../../../../shared/scripts/ui_game/ui_game_players/UIGamePlayersController'
import {UIMGameManager} from '../UIMGameManager'
import {UIUtilsEjsVariables} from '../../../../shared/scripts/UIUtilsEjsVariables'
import {GameStateEnums} from '../../../../../shared/GameEnums'
import {SectionBgView} from '../../../../shared/scripts/ui_canvas_engine/SectionBgView'
import {UIGameSpriteHandler} from '../../../../shared/scripts/UIGameSpriteHandler'
import {
    MechanicCityImprovementsState,
    MechanicCityState,
    MechanicCityWallState,
    MechanicDevelopmentCardsState,
    MechanicLargestArmyState,
    MechanicLongestRoadState,
    MechanicProgressCardsState,
    MechanicRoadState,
    MechanicSettlementState,
    MechanicSpecialBuildPhaseState,
    PlayerColor,
    TurnState,
} from '@colonist/game-states'

export class UIMGamePlayersController extends UIGamePlayersController {
    private background: SectionBgView | undefined

    constructor(uiGameManager: UIMGameManager,
                developmentCardsState: MechanicDevelopmentCardsState | undefined,
                progressCardsState: MechanicProgressCardsState | undefined,
                settlementState: MechanicSettlementState | undefined,
                cityState: MechanicCityState | undefined,
                cityWallState: MechanicCityWallState | undefined,
                cityImprovementsState: MechanicCityImprovementsState | undefined,
                roadState: MechanicRoadState | undefined,
                longestRoadState: MechanicLongestRoadState | undefined,
                largestArmyState: MechanicLargestArmyState | undefined,
                specialBuildPhaseState: MechanicSpecialBuildPhaseState | undefined) {
        super(uiGameManager, developmentCardsState, progressCardsState, settlementState, cityState, cityWallState, cityImprovementsState, roadState, longestRoadState, largestArmyState, specialBuildPhaseState)

        this.createView()
    }

    onEvent(data: any, eventType: GameStateEnums.UIGameEventEnums): void {
        super.onEvent(data, eventType)

        switch(eventType) {
            case GameStateEnums.UIGameEventEnums.RemoveTradeOffer: this.onTradeOfferVisibilityUpdate(true); break
            case GameStateEnums.UIGameEventEnums.TradeOfferVisibilityUpdated: this.onTradeOfferVisibilityUpdate(data); break
            default: break
        }
    }

    private onTradeOfferVisibilityUpdate(visibility: boolean) {
        if(this.container.destroyed) return
        if(UIUtilsEjsVariables.isUserOnTabletApp) return

        this.container.y = this.container.initialPosition.y + (visibility ? 0 : this.uiGameManager.canvasController.itemSize)
    }

    private createView() {
        this.createBackground()
        this.createContainers()
        this.createAllPlayers()
        this.initPlayerInformationWaitIconViews()
        this.initScrollAnimationController()
    }

    protected createContainers(): void {
        const canvas = this.uiGameManager.canvasController
        const w = UIUtilsEjsVariables.isUserOnTabletApp ? canvas.appW * 0.25 : canvas.appW
        const h = this.uiGameManager.gameLayout.getPlayersControllerHeight()
        const x = UIUtilsEjsVariables.isUserOnTabletApp ? canvas.appW - w : 0
        const y = this.uiGameManager.gameLayout.getStateControllerY() + this.uiGameManager.gameLayout.getStateControllerHeight()
        super.createContainers(x, y, w, h, canvas.app.stage, true)
    }

    protected onCanvasResize(): void {
        this.container.destroy({children: true})
        this.playerInformationViews = []
        this.createView()
    }

    private createBackground() {
        this.background?.destroy()
        const canvas = this.uiGameManager.canvasController
        const w = canvas.appW
        const h = this.uiGameManager.gameLayout.getHelpSectionHeight()
        const x = 0
        const y = 0
        this.background = new SectionBgView(x, y, w, h, canvas.app.stage, true)
        this.background.drawBg(undefined, 0.2)
    }

    protected createOpponentPlayerInformationViews(partitionH: number, containerWidth: number, bottomPlayerColor: PlayerColor) {
        let index = 1
        const newPlayerOrder = this.getPlayerOrderWhereMyColorIsLast()
        const opponentViewContainer = this.opponentsScrollbox.content
        const partitionWidth = UIUtilsEjsVariables.isUserOnTabletApp ? this.opponentsScrollbox.width * 0.95 : this.uiGameManager.canvasController.appW / 2
        const highlightTexture = UIGameSpriteHandler.getTexture('player_highlight_left')
        if(highlightTexture == undefined) return
        const partitionHeight = highlightTexture.height / highlightTexture.width * partitionWidth

        const marginBetweenPlayers = this.uiGameManager.canvasController.margin * 5
        const initialPositionX = UIUtilsEjsVariables.isUserOnTabletApp ? 0 : 0
        const offsetFromTop = this.uiGameManager.gameLayout.getPlayersControllerChildOffset()
        const playerIndexOnRight = newPlayerOrder.length / 2
        for(const playerColor of newPlayerOrder) {
            if(playerColor == bottomPlayerColor) continue
            const isPlayerOnRight = index <= playerIndexOnRight

            const player = this.uiGameManager.gameController.getPlayerState(playerColor)
            const playerPublicState = this.uiGameManager.gameController.getPlayerState(playerColor)
            const devCardsPlayerState = this.developmentCardsValidator?.getDevelopmentCardsPlayerState(playerColor)
            const progressCardsPlayerValidator = this.progressCardsValidator?.getProgressCardsPlayerValidator(playerColor)
            const settlementPlayerState = this.settlementValidator?.getSettlementPlayerState(playerColor)
            const cityPlayerState = this.cityValidator?.getCityPlayerState(playerColor)
            const cityWallPlayerState = this.cityWallValidator?.getCityWallPlayerState(playerColor)
            const cityImprovementPlayerState = this.cityImprovementsValidator?.getPlayerCityImprovementsState(playerColor)
            const roadPlayerState = this.roadValidator?.getRoadPlayerState(playerColor)
            const longestRoadPlayerState = this.longestRoadValidator?.getLongestRoadPlayerState(playerColor)
            const largestArmyPlayerState = this.largestArmyValidator?.getLargestArmyPlayerState(playerColor)

            const partitionX = UIUtilsEjsVariables.isUserOnTabletApp ? (this.opponentsScrollbox.width - partitionWidth) : initialPositionX + partitionWidth * (isPlayerOnRight ? 1 : 0)
            const partitionY = offsetFromTop + (UIUtilsEjsVariables.isUserOnTabletApp ?
                partitionHeight * index + marginBetweenPlayers * index : partitionHeight * (isPlayerOnRight ? index - 1 :
                    newPlayerOrder.length - index) + marginBetweenPlayers * (isPlayerOnRight ? index - 1 : newPlayerOrder.length - index))
            const opponentView = new UIMGamePlayerInformationView(this.uiGameManager, player, playerPublicState.state, partitionX, partitionY, partitionWidth, partitionHeight, opponentViewContainer, false,
                devCardsPlayerState,
                progressCardsPlayerValidator,
                settlementPlayerState,
                cityPlayerState,
                cityWallPlayerState,
                cityImprovementPlayerState,
                roadPlayerState,
                longestRoadPlayerState,
                largestArmyPlayerState,
                this.specialBuildPhaseState)
            this.playerInformationViews.push(opponentView)
            index += 1
        }
        this.opponentsScrollbox.setIsScrollable()
        if(UIUtilsEjsVariables.isUserOnTabletApp && newPlayerOrder.length > 5) return
        this.disableDragScroll()
    }

    private disableDragScroll() {
        this.opponentsScrollbox.parent.interactive = false
        this.opponentsScrollbox.interactive = false
        this.opponentsScrollbox.children[0].interactive = false
    }

    protected createMyPlayerInformationView(partitionH: number, partitionW: number, bottomPlayerColor: PlayerColor) {
        const partitionWidth = UIUtilsEjsVariables.isUserOnTabletApp ? this.opponentsScrollbox.width * 0.95 : this.uiGameManager.canvasController.appW / 2
        const highlightTexture = UIGameSpriteHandler.getTexture('player_highlight_left')
        if(highlightTexture == undefined) return
        const partitionHeight = highlightTexture.height / highlightTexture.width * partitionWidth
        const bottomPlayer = this.uiGameManager.gameController.getPlayerState(bottomPlayerColor)
        const bottomPlayerPublicState = this.uiGameManager.gameController.getPlayerState(bottomPlayerColor)
        const devCardsPlayerState = this.developmentCardsValidator?.getDevelopmentCardsPlayerState(bottomPlayerColor)
        const progressCardsPlayerValidator = this.progressCardsValidator?.getProgressCardsPlayerValidator(bottomPlayerColor)
        const settlementPlayerState = this.settlementValidator?.getSettlementPlayerState(bottomPlayerColor)
        const cityPlayerState = this.cityValidator?.getCityPlayerState(bottomPlayerColor)
        const cityWallPlayerState = this.cityWallValidator?.getCityWallPlayerState(bottomPlayerColor)
        const cityImprovementPlayerState = this.cityImprovementsValidator?.getPlayerCityImprovementsState(bottomPlayerColor)
        const roadPlayerState = this.roadValidator?.getRoadPlayerState(bottomPlayerColor)
        const longestRoadPlayerState = this.longestRoadValidator?.getLongestRoadPlayerState(bottomPlayerColor)
        const largestArmyPlayerState = this.largestArmyValidator?.getLargestArmyPlayerState(bottomPlayerColor)

        const offsetFromTop = this.uiGameManager.gameLayout.getPlayersControllerChildOffset()
        const xPosition = UIUtilsEjsVariables.isUserOnTabletApp ? (this.opponentsScrollbox.width - partitionWidth) : 0
        const playerInformation = new UIMGamePlayerInformationView(this.uiGameManager, bottomPlayer, bottomPlayerPublicState.state, xPosition, offsetFromTop, partitionWidth, partitionHeight, this.opponentsScrollbox.content, false,
            devCardsPlayerState,
            progressCardsPlayerValidator,
            settlementPlayerState,
            cityPlayerState,
            cityWallPlayerState,
            cityImprovementPlayerState,
            roadPlayerState,
            longestRoadPlayerState,
            largestArmyPlayerState,
            this.specialBuildPhaseState)
        this.playerInformationViews.push(playerInformation)
    }

    protected scrollToCurrentTurnPlayer(shouldAnimateScroll: boolean = true) {
        if(this.uiGameManager.gameController.currentState.turnState != TurnState.Dice) return
        if(!this.opponentsScrollbox.isScrollable) return
        if(this.opponentsScrollbox.destroyed) return

        const playerView = this.getPlayerInformationViewWithColor(this.uiGameManager.gameController.currentState.currentTurnPlayerColor)
        if(playerView == undefined) return

        const playerIndex = (this.playerInformationViews.indexOf(playerView) + 1) % (this.playerInformationViews.length)
        const scrollIndex = playerIndex <= 0 ? 0 : playerIndex - 1
        if(this.playerInformationViews[scrollIndex].destroyed) return

        const scrollDestination = this.playerInformationViews[scrollIndex].height * scrollIndex
        if(shouldAnimateScroll) {
            this.scrollAnimationController.animate(scrollDestination)
            return
        }

        const scrollDirection = 'scrollTop'
        this.opponentsScrollbox[scrollDirection] = scrollDestination
    }
}
